import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { getSettings, saveSettings } from "../../Redux/Slices/settingsSlice";

const AdminSettings = () => {
  const dispatch = useDispatch();
  const [initialValues, setInitialValues] = useState({
    result_on: false,
    resultFromDate: "",
    services: false,
    resultToDate: "",
  });
  const [loading, setLoading] = useState(true);

  const validationSchema = Yup.object({
    result_on: Yup.boolean(),
    resultFromDate: Yup.string().required("From Date is required"),
    resultToDate: Yup.string().required("To Date is required"),
  });

  const fetchSettingsData = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getSettings()).unwrap();
      if (response?.IsSuccess && response?.Data) {
        setInitialValues({
          result_on: response.Data.result_on ?? false,
          resultFromDate: response.Data.resultFromDate || "",
          resultToDate: response.Data.resultToDate || "",
          services: response.Data.services || false,
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettingsData();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await dispatch(saveSettings(values)).unwrap();
      if (response?.IsSuccess) {
        toast.success(response?.Message || "Settings saved successfully");
      } else {
        toast.error(response?.Message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("An error occurred while saving settings");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-5 md:p-6 lg:p-7 2xl:p-9">
      <div className="bg-white rounded-2xl shadow-custom_shadow overflow-hidden max-w-4xl mx-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-primary/5">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Settings</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage system configurations and result upload availability.
            </p>
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Form className="p-8">
              <div className="space-y-8">
                {/* Result Upload Management Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between hover:bg-gray-50 p-4 rounded-xl transition-colors border border-transparent hover:border-gray-100">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-700">
                          Result Upload Status
                        </h3>
                        <p className="text-sm text-gray-500">
                          Enable or disable result uploading for users on the
                          mobile application.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          name="result_on"
                          className="sr-only peer"
                          checked={values.result_on}
                          onChange={(e) =>
                            setFieldValue("result_on", e.target.checked)
                          }
                        />
                        <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  </div>

                  {/* Date range selection */}
                  <div
                    className={`space-y-6 md:col-span-2 transition-all duration-300 ${values.result_on ? "opacity-100" : "opacity-50 pointer-events-none"}`}
                  >
                    <div className="bg-gray-50 p-6 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-primary"
                          >
                            <rect
                              x="3"
                              y="4"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                            ></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          From Date
                        </label>
                        <Field
                          name="resultFromDate"
                          type="date"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all bg-white"
                        />
                        <ErrorMessage
                          name="resultFromDate"
                          component="div"
                          className="text-red-500 text-xs mt-1 font-medium"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-primary"
                          >
                            <rect
                              x="3"
                              y="4"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                            ></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          To Date
                        </label>
                        <Field
                          name="resultToDate"
                          type="date"
                          className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all bg-white"
                        />
                        <ErrorMessage
                          name="resultToDate"
                          component="div"
                          className="text-red-500 text-xs mt-1 font-medium"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100 flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn flex items-center gap-2 px-10 py-3 shadow-lg shadow-primary/20"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Settings"
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AdminSettings;
